import { z } from "zod";
import { AgentId, ConversationId, MessageId, TaskId } from "./common.js";
import type { McpScope } from "./entities.js";
import { Ref } from "./entities.js";
import { AssetId, AssetKind, MailPriority, MeetingId, Model3dMeta, Model3dOp, TaskStatus } from "./collab.js";

/**
 * 平台 MCP 工具（agent 的"基类能力"，doc/api_v1.md §5）。
 * mcp-gw 按 agent 的 mcpScopes 暴露子集；所有写操作由 bus 盖章（发件人/hop/预算）。
 * description 面向模型，用英文；agent 的对外产出语言由项目 locale 决定。
 */

export interface McpToolDef<I extends z.ZodObject = z.ZodObject, O extends z.ZodType = z.ZodType> {
  scope: McpScope;
  description: string;
  input: I;
  output: O;
}
const tool = <I extends z.ZodObject, O extends z.ZodType>(def: McpToolDef<I, O>) => def;

/** 名字或 ID 都可：模型更擅长用名字（@frontend），网关负责解析 */
const AgentRef = z.string().min(1).describe("Teammate name (e.g. 'frontend') or agent id");
const TaskRefIn = z.string().describe("Task id (tsk_…)");
const Ack = z.object({ ok: z.literal(true) });

const TeammateCard = z.object({ id: AgentId, name: z.string(), role: z.string(), status: z.string(), description: z.string() });
const TaskView = z.object({ id: TaskId, title: z.string(), status: TaskStatus, assignee: z.string().nullable(), parentId: TaskId.nullable(), branch: z.string().nullable(), prNumber: z.number().nullable(), spec: z.string().optional() });
const MailView = z.object({ id: MessageId, threadId: ConversationId, from: z.string(), subject: z.string(), body: z.string().optional(), priority: MailPriority, unread: z.boolean(), at: z.string() });

export const mcpTools = {
  // ── identity
  whoami: tool({
    scope: "identity",
    description: "Return your own identity (name, role, project) and a list of teammates with their status.",
    input: z.object({}),
    output: z.object({ id: AgentId, name: z.string(), role: z.string(), project: z.object({ id: z.string(), name: z.string(), locale: z.string() }), teammates: z.array(TeammateCard) }),
  }),
  directory_list: tool({
    scope: "identity",
    description: "List teammates in this project, optionally filtered by role.",
    input: z.object({ role: z.string().optional() }),
    output: z.object({ items: z.array(TeammateCard) }),
  }),

  // ── mail
  mail_send: tool({
    scope: "mail",
    description: "Send an asynchronous mail to one or more teammates. Use high/urgent priority only when the recipient must act now (it wakes them immediately).",
    input: z.object({ to: z.array(AgentRef).min(1), subject: z.string().max(200), body: z.string(), attachments: z.array(Ref).optional(), priority: MailPriority.default("normal") }),
    output: z.object({ id: MessageId, threadId: ConversationId }),
  }),
  mail_list: tool({
    scope: "mail",
    description: "List mails in your inbox (newest first).",
    input: z.object({ unreadOnly: z.boolean().default(true), cursor: z.string().optional() }),
    output: z.object({ items: z.array(MailView), nextCursor: z.string().nullable() }),
  }),
  mail_read: tool({
    scope: "mail",
    description: "Read a mail and mark it as read.",
    input: z.object({ id: z.string() }),
    output: MailView.extend({ attachments: z.array(Ref), to: z.array(z.string()) }),
  }),
  mail_reply: tool({
    scope: "mail",
    description: "Reply to a mail thread.",
    input: z.object({ id: z.string(), body: z.string(), attachments: z.array(Ref).optional(), priority: MailPriority.optional() }),
    output: z.object({ id: MessageId }),
  }),

  // ── chat
  chat_post: tool({
    scope: "chat",
    description: "Post a message in a chat room. Mention teammates with @name in text or via `mentions` to wake them; unmentioned members are not woken.",
    input: z.object({ conversation: z.string(), text: z.string(), mentions: z.array(AgentRef).optional(), replyTo: z.string().optional() }),
    output: z.object({ id: MessageId }),
  }),
  chat_history: tool({
    scope: "chat",
    description: "Read recent messages of a chat room.",
    input: z.object({ conversation: z.string(), limit: z.number().int().min(1).max(100).default(30) }),
    output: z.object({ items: z.array(z.object({ id: MessageId, from: z.string(), text: z.string(), at: z.string() })) }),
  }),

  // ── meeting
  meeting_speak: tool({
    scope: "meeting",
    description: "Speak in a meeting. Only allowed when it is your turn.",
    input: z.object({ meeting: z.string(), text: z.string() }),
    output: Ack,
  }),
  meeting_propose_action: tool({
    scope: "meeting",
    description: "Propose an action item to be recorded in the meeting minutes.",
    input: z.object({ meeting: z.string(), title: z.string(), assignee: AgentRef.optional(), spec: z.string().optional() }),
    output: Ack,
  }),

  // ── task
  task_list_mine: tool({
    scope: "task",
    description: "List tasks assigned to you.",
    input: z.object({ status: TaskStatus.optional() }),
    output: z.object({ items: z.array(TaskView) }),
  }),
  task_update: tool({
    scope: "task",
    description: "Accept a task (status=working) or post a progress note.",
    input: z.object({ task: TaskRefIn, status: z.literal("working").optional(), note: z.string().optional() }),
    output: TaskView,
  }),
  task_block: tool({
    scope: "task",
    description: "Mark a task as blocked: input_required when you need clarification, auth_required when you need a permission or credential.",
    input: z.object({ task: TaskRefIn, kind: z.enum(["input_required", "auth_required"]), question: z.string() }),
    output: TaskView,
  }),
  task_deliver: tool({
    scope: "task",
    description: "Deliver a task: summary plus PR number and/or artifacts. Wakes the delegator.",
    input: z.object({ task: TaskRefIn, summary: z.string(), pr: z.number().int().positive().optional(), artifacts: z.array(Ref).optional() }),
    output: TaskView,
  }),

  // ── code
  pr_open: tool({
    scope: "code",
    description: "Open a pull request from your task branch to the default branch.",
    input: z.object({ title: z.string(), body: z.string(), branch: z.string(), repo: z.string().optional(), task: TaskRefIn.optional() }),
    output: z.object({ number: z.number().int(), url: z.string().nullable() }),
  }),
  pr_comment: tool({
    scope: "code",
    description: "Comment on a pull request, optionally on a specific file line.",
    input: z.object({ pr: z.number().int(), body: z.string(), path: z.string().optional(), line: z.number().int().optional(), repo: z.string().optional() }),
    output: Ack,
  }),
  pr_status: tool({
    scope: "code",
    description: "Get pull request status, reviews and mergeability.",
    input: z.object({ pr: z.number().int(), repo: z.string().optional() }),
    output: z.object({ number: z.number(), state: z.string(), mergeable: z.boolean().nullable(), reviews: z.array(z.object({ by: z.string(), event: z.string(), body: z.string() })) }),
  }),

  // ── asset
  asset_generate_image: tool({
    scope: "asset",
    description: "Start an image generation job. Returns immediately; you will be woken with an asset_ready message when done.",
    input: z.object({ prompt: z.string(), negative: z.string().optional(), size: z.string().default("1024x1024"), count: z.number().int().min(1).max(4).default(1), style: z.string().optional(), provider: z.string().optional(), refAssets: z.array(z.string()).optional(), name: z.string().optional() }),
    output: z.object({ jobId: z.string(), estCostMicroUsd: z.number() }),
  }),
  asset_generate_3d: tool({
    scope: "asset",
    description:
      "Start a 3D model generation job from a prompt, a reference image asset, or multi-view images (front required; left/back/right optional, same character in the same pose). Takes minutes; you will be woken with asset_ready when done.",
    input: z.object({
      prompt: z.string().optional(),
      fromImage: z.string().optional(),
      fromImages: z.object({ front: z.string(), left: z.string().optional(), back: z.string().optional(), right: z.string().optional() }).optional(),
      format: z.enum(["glb", "fbx", "obj"]).default("glb"),
      quality: z.enum(["draft", "standard", "high"]).default("standard"),
      /** 目标面数（三角面）；不填由供应商决定 */
      targetPolycount: z.number().int().min(100).max(300_000).optional(),
      topology: z.enum(["triangle", "quad"]).optional(),
      texture: z.boolean().default(true),
      pbr: z.boolean().default(false),
      style: z.enum(["realistic", "stylized", "lowpoly"]).optional(),
      provider: z.string().optional(),
      name: z.string().optional(),
    }),
    output: z.object({ jobId: z.string(), estCostMicroUsd: z.number() }),
  }),
  asset_remesh_3d: tool({
    scope: "asset",
    description: "Rebuild the mesh of a 3D asset to a target triangle count / topology (provider-side retopology). Creates a new asset version derived from the source.",
    input: z.object({ asset: z.string(), targetPolycount: z.number().int().min(100).max(300_000), topology: z.enum(["triangle", "quad"]).default("triangle"), format: z.enum(["glb", "fbx", "obj"]).default("glb"), provider: z.string().optional(), name: z.string().optional() }),
    output: z.object({ jobId: z.string(), estCostMicroUsd: z.number() }),
  }),
  asset_retexture_3d: tool({
    scope: "asset",
    description: "Generate new textures for a 3D asset from a text prompt and/or a style image asset, keeping the mesh.",
    input: z.object({ asset: z.string(), prompt: z.string().optional(), styleImage: z.string().optional(), pbr: z.boolean().default(false), provider: z.string().optional(), name: z.string().optional() }),
    output: z.object({ jobId: z.string(), estCostMicroUsd: z.number() }),
  }),
  asset_rig_3d: tool({
    scope: "asset",
    description: "Auto-rig a 3D character (humanoid or quadruped; the model should be in T/A-pose, facing +Z). Produces a skinned GLB that can then be animated with asset_animate_3d.",
    input: z.object({ asset: z.string(), rigType: z.enum(["humanoid", "quadruped"]).default("humanoid"), heightMeters: z.number().positive().max(100).optional(), provider: z.string().optional(), name: z.string().optional() }),
    output: z.object({ jobId: z.string(), estCostMicroUsd: z.number() }),
  }),
  asset_animate_3d: tool({
    scope: "asset",
    description: "Apply preset animations to a rigged 3D asset (from asset_rig_3d, same provider). Use asset_media_catalog to list the provider's animation presets. Produces one GLB containing all requested clips.",
    input: z.object({ asset: z.string(), animations: z.array(z.string()).min(1).max(8), format: z.enum(["glb", "fbx"]).default("glb"), provider: z.string().optional(), name: z.string().optional() }),
    output: z.object({ jobId: z.string(), estCostMicroUsd: z.number() }),
  }),
  asset_media_catalog: tool({
    scope: "asset",
    description: "List available media providers, which operations each supports, animation presets, and estimated prices.",
    input: z.object({ kind: z.enum(["image", "model3d"]).optional() }),
    output: z.object({
      providers: z.array(
        z.object({
          id: z.string(),
          kind: z.enum(["image", "model3d"]),
          ops: z.array(Model3dOp),
          default: z.boolean(),
          animations: z.array(z.string()).optional(),
          /** 各操作的预估价格（micro USD，按标准质量） */
          prices: z.record(z.string(), z.number()).optional(),
        }),
      ),
    }),
  }),
  asset_job_status: tool({
    scope: "asset",
    description: "Check a media generation job.",
    input: z.object({ jobId: z.string() }),
    output: z.object({ state: z.string(), op: Model3dOp, progress: z.number().nullable(), assetIds: z.array(AssetId), error: z.string().nullable() }),
  }),
  asset_import: tool({
    scope: "asset",
    description: "Import a file from your workspace into the project asset library (e.g. after slicing or converting).",
    input: z.object({
      path: z.string(),
      kind: AssetKind,
      name: z.string().optional(),
      tags: z.array(z.string()).optional(),
      derivedFrom: z.string().optional(),
      /** 仅 model3d：工作区里的预览图（PNG/JPEG，例如转台渲染），作为该版本的缩略图 */
      thumbnail: z.string().optional(),
    }),
    output: z.object({ assetId: AssetId, version: z.number(), model: Model3dMeta.nullable().optional() }),
  }),
  asset_publish: tool({
    scope: "asset",
    description: "Download an asset version into your workspace at targetPath (use Git LFS for large files), so it can be committed.",
    input: z.object({ asset: z.string(), version: z.number().int().optional(), targetPath: z.string() }),
    output: z.object({ path: z.string(), size: z.number() }),
  }),

  // ── human
  ask_human: tool({
    scope: "human",
    description: "Ask the human owner a question. Your turn ends; you will be woken with the answer.",
    input: z.object({ question: z.string(), options: z.array(z.string()).optional(), urgent: z.boolean().default(false) }),
    output: z.object({ questionId: z.string() }),
  }),

  // ── orchestrate（编排 agent 专属）
  task_create: tool({
    scope: "orchestrate",
    description: "Create a task (optionally under a parent and assigned). Assigning wakes the assignee.",
    input: z.object({ title: z.string(), spec: z.string(), parent: TaskRefIn.optional(), assignee: AgentRef.optional() }),
    output: TaskView,
  }),
  task_assign: tool({
    scope: "orchestrate",
    description: "Assign or reassign a task to a teammate.",
    input: z.object({ task: TaskRefIn, agent: AgentRef }),
    output: TaskView,
  }),
  task_tree: tool({
    scope: "orchestrate",
    description: "View the task tree of the project (or under a root task).",
    input: z.object({ root: TaskRefIn.optional() }),
    output: z.object({ items: z.array(TaskView) }),
  }),
  task_review: tool({
    scope: "orchestrate",
    description: "Confirm a delivered task, or request rework (creates a new task referencing the original).",
    input: z.object({ task: TaskRefIn, decision: z.enum(["confirm", "rework"]), feedback: z.string().optional() }),
    output: TaskView,
  }),
  agent_status: tool({
    scope: "orchestrate",
    description: "Status of every teammate: busy/idle, current task, budget used.",
    input: z.object({}),
    output: z.object({ items: z.array(TeammateCard.extend({ currentTask: z.string().nullable(), budgetUsedMicroUsd: z.number() })) }),
  }),
  meeting_call: tool({
    scope: "orchestrate",
    description: "Call a meeting with an agenda; you are the moderator. Participants speak in turns.",
    input: z.object({ title: z.string(), agenda: z.array(z.string()).min(1), participants: z.array(AgentRef).min(1), maxRounds: z.number().int().min(1).max(10).default(3) }),
    output: z.object({ meeting: MeetingId, conversation: ConversationId }),
  }),
  meeting_close: tool({
    scope: "orchestrate",
    description: "Close a meeting with minutes; action items become tasks.",
    input: z.object({ meeting: z.string(), summary: z.string(), decisions: z.array(z.string()), actionItems: z.array(z.object({ title: z.string(), assignee: AgentRef.optional(), spec: z.string().optional() })) }),
    output: z.object({ taskIds: z.array(TaskId) }),
  }),
  review_request: tool({
    scope: "orchestrate",
    description: "Ask a teammate to review a pull request.",
    input: z.object({ pr: z.number().int(), reviewer: AgentRef, repo: z.string().optional() }),
    output: Ack,
  }),
  merge_pr: tool({
    scope: "orchestrate",
    description: "Merge an approved pull request.",
    input: z.object({ pr: z.number().int(), repo: z.string().optional(), method: z.enum(["merge", "squash", "rebase"]).default("squash") }),
    output: z.object({ merged: z.boolean(), sha: z.string().nullable() }),
  }),
} as const satisfies Record<string, McpToolDef>;

export type McpToolName = keyof typeof mcpTools;
export type McpToolInput<N extends McpToolName> = z.infer<(typeof mcpTools)[N]["input"]>;
export type McpToolOutput<N extends McpToolName> = z.infer<(typeof mcpTools)[N]["output"]>;

export function toolsForScopes(scopes: readonly McpScope[]): McpToolName[] {
  return (Object.keys(mcpTools) as McpToolName[]).filter((n) => scopes.includes(mcpTools[n].scope));
}
